import {
  Alert,
  Box,
  CircularProgress,
  Paper,
  Tab,
  Tabs
} from "@mui/material";
import { format, parseISO } from "date-fns";
import { useMemo, useState } from "react";

import { useFeatureFlagEnabled } from "../../../common/posthog";
import CopyButton from "../../../components/CopyButton";
import MarkdownRenderer from "../../../components/MarkdownRenderer";
import { useUsers } from "../../account/hooks/useUsers";
import { useCoachingArtifact } from "../hooks/useCoachingArtifact";
import { CoachingArtifact } from "../hooks/useCoachingArtifacts";
import { buildCoachingArtifactUrl } from "../utils/artifactUrl";
import { buildReportTabs } from "../utils/coachingReportTabs";
import CoachingArtifactHeader from "./CoachingArtifactHeader";
import CoachingArtifactMetadata from "./CoachingArtifactMetadata";
import CoachingConfigDialog from "./CoachingConfigDialog";

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel = ({ children, value, index, ...other }: TabPanelProps) => {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`coaching-tabpanel-${index}`}
      aria-labelledby={`coaching-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
};

const a11yProps = (index: number) => ({
  id: `coaching-tab-${index}`,
  "aria-controls": `coaching-tabpanel-${index}`
});

interface CoachingArtifactViewerProps {
  artifactId?: string;
  artifact?: CoachingArtifact;
  tenantId: string | null;
  permittedEmails: string[] | null;
  isDrawer?: boolean;
  onClose?: () => void;
}

const CoachingArtifactViewer = ({ artifactId, artifact: propArtifact, tenantId, permittedEmails, isDrawer = false, onClose }: CoachingArtifactViewerProps) => {
  const [isMetadataDialogOpen, setIsMetadataDialogOpen] = useState(false);
  const [currentTab, setCurrentTab] = useState(0);
  const { users } = useUsers();
  const coachingAdminEnabled = useFeatureFlagEnabled("coaching-admin");

  const { data: fetchedArtifact, isLoading, error } = useCoachingArtifact({
    id: artifactId || null,
    tenantId,
    permittedEmails,
    enabled: !propArtifact && !!artifactId
  });

  const artifact = propArtifact || fetchedArtifact;

  const handleExploreData = () => {
    if (!artifact) {
      return;
    }

    const startDate = format(parseISO(artifact.start_date), "yyyy-MM-dd");
    const endDate = format(parseISO(artifact.end_date), "yyyy-MM-dd");
    const timeRange = `custom:${startDate}:${endDate}`;

    const params = new URLSearchParams({
      user_email: artifact.agent_email,
      sc_name: artifact.lens_name,
      time_range: timeRange
    });

    window.open(`/explore?${params.toString()}`, "_blank");
  };

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setCurrentTab(newValue);
  };

  const handlePopOut = () => {
    if (artifact?.id) {
      window.open(buildCoachingArtifactUrl(artifact.id), "_blank");
      onClose?.();
    }
  };

  const content = artifact?.content as Record<string, unknown> | null;
  const reportTitle = typeof content?.title === "string" ? content.title : undefined;
  const tabs = useMemo(() => buildReportTabs(content), [content]);
  const markdownContent = tabs.length > 0
    ? tabs.map((t) => t.body).join("\n\n---\n\n")
    : "";

  const createdByUser = artifact?.created_by
    ? users?.find((user) => user.id === artifact.created_by)
    : null;

  if (!propArtifact && isLoading) {
    return (
      <Box sx={{ p: 3, display: "flex", justifyContent: "center" }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!propArtifact && error) {
    return (
      <Box sx={{ p: 3 }}>
        <Paper sx={{ p: 3 }}>
          <Alert severity="error">
            Failed to load artifact: {error.message}
          </Alert>
        </Paper>
      </Box>
    );
  }

  if (!artifact) {
    return (
      <Box sx={{ p: 3 }}>
        <Paper sx={{ p: 3 }}>
          <Alert severity="warning">Artifact not found</Alert>
        </Paper>
      </Box>
    );
  }

  const renderContent = () => {
    if (artifact.status === "generating" && tabs.length === 0) {
      return (
        <Box sx={{ p: 3, display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}>
          <CircularProgress />
          <Alert severity="info" sx={{ width: "100%" }}>
            The report is still being generated. Please close this page and try again later.
          </Alert>
        </Box>
      );
    }
    if (artifact.status === "failed") {
      return (
        <Box sx={{ p: 3 }}>
          <Alert severity="error">
            Report generation failed. Please cancel this report from the Created Reports list
            and submit again.
          </Alert>
        </Box>
      );
    }
    if (tabs.length === 0) {
      return (
        <Box sx={{ p: 3 }}>
          <Alert severity="info">No content available</Alert>
        </Box>
      );
    }
    return (
      <>
        <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
          <Tabs
            value={currentTab}
            onChange={handleTabChange}
            aria-label="coaching report tabs"
            variant="scrollable"
            scrollButtons="auto"
          >
            {tabs.map((tab, index) => (
              <Tab key={tab.key} label={tab.label} {...a11yProps(index)} />
            ))}
          </Tabs>
        </Box>
        {tabs.map((tab, index) => (
          <TabPanel key={tab.key} value={currentTab} index={index}>
            <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 1 }}>
              <CopyButton text={tab.body} tooltipTitle="Copy" />
            </Box>
            <MarkdownRenderer content={tab.body} />
          </TabPanel>
        ))}
      </>
    );
  };

  return (
    <Box sx={{ p: isDrawer ? 0 : 1 }}>
      <Paper sx={{ p: 0, borderRadius: isDrawer ? 0 : 1 }}>
        <CoachingArtifactHeader
          markdownContent={markdownContent}
          onOpenMetadata={() => setIsMetadataDialogOpen(true)}
          onExploreData={handleExploreData}
          onPopOut={handlePopOut}
          onClose={onClose}
          isDrawer={isDrawer}
          showMetadataButton={coachingAdminEnabled}
        />

        <CoachingArtifactMetadata
          artifact={artifact}
          createdByEmail={createdByUser?.email}
          reportTitle={reportTitle}
        />

        {renderContent()}
      </Paper>

      <CoachingConfigDialog
        open={isMetadataDialogOpen}
        onClose={() => setIsMetadataDialogOpen(false)}
        artifact={artifact}
      />

    </Box>
  );
};

export default CoachingArtifactViewer;
